import React from 'react';
import { useNavigate } from 'react-router';
import { useForm } from 'react-hook-form';
import Box from '@amzn/meridian/box';
import Button from '@amzn/meridian/button';
import Column from '@amzn/meridian/column';
import Heading from '@amzn/meridian/heading';
import Row from '@amzn/meridian/row';
import Breadcrumb, { BreadcrumbGroup } from '@amzn/meridian/breadcrumb';
import { useTranslation } from 'src/arb-wrapper';
import { FormInput } from 'src/components/common/forms';
import { FormSelect } from 'src/components/common/forms/FormSelect';
import { PublisherAccountFields, buildPublisherAccountConfig } from 'src/components/publisher-config/PublisherAccountFields';
import { CreationSource } from 'src/constants';
import { useCampaignConfigurationControllerCreateMutation } from 'src/store/leela-api';
import { AlertType } from 'src/components/common/alerts/alert.types';
import { useToasterContext } from 'src/components/common/alerts/ToasterContext';
import { BusinessLines, Countries } from 'src/store/constants';
import { useAvailablePublishers } from 'src/hooks/use-available-publishers';
import type { Publisher, Country, BusinessLine } from 'src/store/generated/leela-api-generated';

export const PublisherConfigCreate: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { createToast } = useToasterContext();
  const availablePublishers = useAvailablePublishers();
  const [createConfig, { isLoading }] = useCampaignConfigurationControllerCreateMutation();

  const defaultValues: Record<string, unknown> = {
    name: '',
    publisher: '',
    countryMode: 'all',
    supportedCountries: [],
    businessLineMode: 'all',
    supportedBusinessLines: [],
  };

  const { control, handleSubmit, watch, setValue } = useForm({
    defaultValues,
    mode: 'onTouched',
  });

  const selectedPublisher = watch('publisher') as Publisher;
  const countryMode = watch('countryMode') as string;
  const businessLineMode = watch('businessLineMode') as string;

  const onSubmit = async (formData: Record<string, unknown>) => {
    try {
      const config = buildPublisherAccountConfig(formData.publisher as string, formData);

      const result = await createConfig({
        createCampaignConfigurationDto: {
          name: formData.name as string,
          supportedPublishers: [formData.publisher as Publisher],
          supportedCountries: countryMode === 'all' ? [] as Country[] : formData.supportedCountries as Country[],
          supportedBusinessLines: businessLineMode === 'all' ? [] as BusinessLine[] : formData.supportedBusinessLines as BusinessLine[],
          config,
          source: CreationSource.ADMIN,
        },
      }).unwrap();

      createToast({ type: AlertType.SUCCESS, message: t('publisher_config_create_success') });
      navigate(`/publisher-configs/${result.id}`);
    } catch (error: unknown) {
      const err = error as { data?: { message?: string } };
      createToast({
        type: AlertType.ERROR,
        message: err?.data?.message ?? t('publisher_config_create_error'),
      });
    }
  };

  return (
    <Column>
      <BreadcrumbGroup>
        <Breadcrumb onClick={() => { navigate('/publisher-configs'); }}>{t('publisher_configs')}</Breadcrumb>
        <Breadcrumb>{t('publisher_config_create')}</Breadcrumb>
      </BreadcrumbGroup>
      <Heading level={1}>{t('publisher_config_create')}</Heading>
      <Box spacingInset="400" type="outline">
        <Column spacing="300">
          <FormInput
            control={control}
            data-testid="publisher-config-name"
            label={t('form_global_name')}
            name="name"
            required
          />

          <Row widths={['fill', 'fill']}>
            <FormSelect
              control={control}
              data-testid="publisher-config-publisher"
              label={t('form_campaigns_publisher')}
              name="publisher"
              options={availablePublishers
                .map(p => ({ label: p, value: p }))}
              required
            />
            <div />
          </Row>

          <Row widths={['fill', 'fill']}>
            <Row widths={['fill', 'fill']}>
              <FormSelect
                control={control}
                data-testid="publisher-config-country-mode"
                label={t('publisher_config_countries')}
                name="countryMode"
                options={[{ label: t('all'), value: 'all' }, { label: t('publisher_config_specific'), value: 'specific' }]}
              />
              {countryMode === 'specific'
                ? (
                <FormSelect
                  control={control}
                  data-testid="publisher-config-countries"
                  label={t('publisher_config_select_countries')}
                  name="supportedCountries"
                  options={Countries.map(c => ({ label: c, value: c }))}
                />
                  )
                : <div />}
            </Row>
            <Row widths={['fill', 'fill']}>
              <FormSelect
                control={control}
                data-testid="publisher-config-business-line-mode"
                label={t('publisher_config_business_lines')}
                name="businessLineMode"
                options={[{ label: t('all'), value: 'all' }, { label: t('publisher_config_specific'), value: 'specific' }]}
              />
              {businessLineMode === 'specific'
                ? (
                <FormSelect
                  control={control}
                  data-testid="publisher-config-business-lines"
                  label={t('publisher_config_select_business_lines')}
                  name="supportedBusinessLines"
                  options={Object.values(BusinessLines).map(bl => ({ label: bl, value: bl }))}
                />
                  )
                : <div />}
            </Row>
          </Row>

          {selectedPublisher && <PublisherAccountFields control={control} publisher={selectedPublisher} setValue={setValue} watch={watch} />}

          <Row alignmentHorizontal="end">
            <Button type="tertiary" onClick={() => { navigate('/publisher-configs'); }}>
              {t('form_global_cancel')}
            </Button>
            <Button
              data-testid="publisher-config-save"
              disabled={isLoading}
              type="primary"
              onClick={handleSubmit(onSubmit)}
            >
              {t('form_global_save')}
            </Button>
          </Row>
        </Column>
      </Box>
    </Column>
  );
};
